/**
 * DEMO · Idea 11 — Así va creciendo la obra (4D).
 *
 * Real (del modelo): la SECUENCIA (nivel por elevación → grupo de clases IFC), qué piezas entran en cada
 * etapa y las cantidades de cada concepto (medición de ci). De ejemplo (JSON): duración de cada etapa,
 * precios, avance reportado y curva real.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Slider } from '@fluentui/react-components';
import { Pause20Filled, Play20Filled } from '@fluentui/react-icons';
import { DemoVisor3D, type Pintura } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel, estiloTabla } from '../../components/demos/DemoMarco.js';
import { CurvaAvance } from '../../components/demos/CurvaAvance.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import { coloresDemo, pesos } from '../../components/demos/logicaDemo.js';
import datos from '../../components/demos/datos/avance-4d.json';
import {
  armarEtapas,
  avancesEn,
  conceptosDelModelo,
  corteDe,
  programadoPorConcepto,
} from '../../components/demos/avanceObra.js';
import { brandingProductoActivo } from '../../lib/brandingDelProducto.js';
import catalogo from '../../components/demos/datos/catalogo.json';

const FICHA = catalogo.demos.find((d) => d.id === 'avance-4d')!;

export function DemoAvance4D() {
  const [modelo, setModelo] = useState<ModeloDemo | null>(null);
  const [t, setT] = useState<number>(datos.corte.semana);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [resaltado, setResaltado] = useState<string | null>(null);
  const colores = useMemo(() => coloresDemo(), []);

  const etapas = useMemo(() => (modelo ? armarEtapas(modelo) : []), [modelo]);
  const total = etapas.length ? etapas[etapas.length - 1]!.fin : 1;

  // Conceptos de la HP de ejemplo con su cantidad REAL del modelo.
  const conceptos = useMemo(() => (modelo ? conceptosDelModelo(modelo) : []), [modelo]);
  const importeTotal = conceptos.reduce((s, x) => s + x.importe, 0);

  const programadoEn = (sem: number) => programadoPorConcepto(etapas, conceptos, sem);
  const curvaProgramada = useMemo(() => {
    if (!conceptos.length || !importeTotal) return [0];
    return Array.from({ length: total + 1 }, (_, s) => {
      const p = programadoEn(s);
      return conceptos.reduce((a, x) => a + ((p.get(x.c.id) ?? 0) / 100) * x.importe, 0) / importeTotal * 100;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptos, etapas, total, importeTotal]);

  const corte = datos.corte;
  const real = corte.avanceReal as Record<string, number>;
  const progCorte = useMemo(() => programadoEn(corte.semana), [conceptos, etapas]); // eslint-disable-line react-hooks/exhaustive-deps
  const ejecutado = conceptos.reduce((a, x) => a + ((real[x.c.id] ?? 0) / 100) * x.importe, 0);

  // Reproducir: avanza el tiempo a la velocidad del JSON.
  const rafRef = useRef(0);
  useEffect(() => {
    if (!reproduciendo) return;
    let prev = performance.now();
    const paso = (now: number) => {
      const dt = (now - prev) / 1000;
      prev = now;
      setT((v) => {
        const nv = v + dt * datos.reproduccion.semanasPorSegundo;
        if (nv >= total) {
          setReproduciendo(false);
          return total;
        }
        return nv;
      });
      rafRef.current = requestAnimationFrame(paso);
    };
    rafRef.current = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(rafRef.current);
  }, [reproduciendo, total]);

  const avances = useMemo(() => avancesEn(etapas, t), [etapas, t]);
  const resaltadas = useMemo(
    () => new Set(conceptos.find((x) => x.c.id === resaltado)?.els.map((e) => e.clave) ?? []),
    [conceptos, resaltado],
  );
  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null, clave: string): Pintura => {
        if (!el) return { visible: false };
        if (resaltadas.has(clave)) return { color: colores.acento, opacidad: 1 };
        const a = avances.get(clave);
        const f = a?.f ?? 0;
        if (f >= 1) return { opacidad: 1 };
        if (f > 0 && a) {
          // A medio construir: lo hecho en ámbar y lo que falta como fantasma tenue.
          const c = corteDe(el, f, a.crece);
          return c
            ? { color: colores.warn, opacidad: 0.9, recorte: { ...c, fantasma: { color: colores.neutro, opacidad: 0.1 } } }
            : { color: colores.warn, opacidad: 0.55 };
        }
        return { color: colores.neutro, opacidad: 0.07 };
      },
    [avances, resaltadas, colores],
  );

  const etapaActual = etapas.find((e) => t > e.inicio && t < e.fin) ?? (t >= total ? null : etapas[0]);
  const semana = Math.min(Math.ceil(t - 1e-9), total);

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(360px, 1fr)', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <DemoVisor3D
            fuentes={[{ clave: 'a', url: '/demos/fzk-haus-a.ifc' }]}
            onListo={(m) => setModelo(m.a ?? null)}
            pintar={pintar}
            alto={520}
          />
          <Panel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button
                appearance="primary"
                icon={reproduciendo ? <Pause20Filled /> : <Play20Filled />}
                disabled={!modelo}
                onClick={() => {
                  if (!reproduciendo && t >= total) setT(0);
                  setReproduciendo((v) => !v);
                }}
              >
                {reproduciendo ? 'Pausar' : 'Reproducir'}
              </Button>
              <Slider
                style={{ flex: 1 }}
                min={0}
                max={total}
                step={0.1}
                value={t}
                disabled={!modelo}
                onChange={(_, d) => {
                  setReproduciendo(false);
                  setT(d.value);
                }}
              />
              <div style={{ minWidth: 150, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                Semana {t <= 0 ? 0 : semana} de {total}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
              <Leyenda color="var(--text-primary)" texto="Ejecutado" />
              <Leyenda color="var(--warn)" texto="En proceso" />
              <Leyenda color="var(--text-tertiary)" texto="Pendiente" tenue />
              <span style={{ marginLeft: 'auto' }}>
                {t >= total ? 'Obra terminada' : etapaActual ? `Ahora: ${etapaActual.nombre}` : ''}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {etapas.map((e) => (
                <div
                  key={e.clave}
                  title={`${e.nombre} · semanas ${e.inicio + 1} a ${e.fin}`}
                  style={{
                    flex: e.fin - e.inicio,
                    fontSize: 11,
                    padding: '4px 6px',
                    borderRadius: 'var(--radius-sm)',
                    background: t >= e.fin ? 'var(--ok-weak)' : t > e.inicio ? 'var(--warn-weak)' : 'var(--bg-raised)',
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {e.nombre}
                </div>
              ))}
            </div>
            <NotaEjemplo>
              La secuencia sale del modelo: primero el nivel más bajo, y dentro de cada nivel losa → muros → trabes → techo;
              la cancelería va al final. Las semanas de cada etapa son de ejemplo.
            </NotaEjemplo>
          </Panel>
        </div>

        <Panel titulo="Avance reportado desde OPUS Control">
          <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>Corte al {corte.fecha} · datos de ejemplo</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Cifra etiqueta="Importe ejecutado" valor={pesos(ejecutado)} />
            <Cifra etiqueta="Importe total" valor={pesos(importeTotal)} sub={importeTotal ? `${Math.round((ejecutado / importeTotal) * 100)} % ejecutado` : ''} />
          </div>
          <CurvaAvance
            programado={curvaProgramada}
            real={corte.curvaReal}
            semanaActual={Math.min(t, total)}
            semanaCorte={corte.semana}
            colorReal={brandingProductoActivo('ci').c1}
          />
          <table style={estiloTabla.tabla}>
            <thead>
              <tr>
                <th style={estiloTabla.th}>Concepto</th>
                <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Programado</th>
                <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Real</th>
              </tr>
            </thead>
            <tbody>
              {conceptos.map((x) => {
                const p = progCorte.get(x.c.id) ?? 0;
                const r = real[x.c.id] ?? 0;
                const atrasado = r < p - corte.toleranciaAtrasoPuntos;
                const activo = resaltado === x.c.id;
                return (
                  <tr
                    key={x.c.id}
                    onClick={() => setResaltado(activo ? null : x.c.id)}
                    style={{ cursor: 'pointer', background: activo ? 'var(--accent-weak)' : atrasado ? 'var(--bad-weak)' : undefined }}
                    title="Clic para ubicarlo en el 3D"
                  >
                    <td style={{ ...estiloTabla.td, color: atrasado ? 'var(--bad)' : undefined }}>
                      {x.c.descripcion}
                      {atrasado && <div style={{ fontSize: 11, fontWeight: 600 }}>Atrasado {Math.round(p - r)} puntos</div>}
                      <Barra prog={p} real={r} atrasado={atrasado} />
                    </td>
                    <td style={estiloTabla.num}>{Math.round(p)} %</td>
                    <td style={{ ...estiloTabla.num, color: atrasado ? 'var(--bad)' : undefined, fontWeight: atrasado ? 600 : undefined }}>
                      {Math.round(r)} %
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <NotaEjemplo>
            El programado se calcula con las cantidades del modelo y el programa de ejemplo; el real es un reporte de
            ejemplo. Clic en un concepto para verlo en el 3D.
          </NotaEjemplo>
        </Panel>
      </div>
    </DemoMarco>
  );
}

function Leyenda({ color, texto, tenue }: { color: string; texto: string; tenue?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 12, height: 12, borderRadius: 3, background: color, opacity: tenue ? 0.35 : 1 }} />
      {texto}
    </span>
  );
}

function Cifra({ etiqueta, valor, sub }: { etiqueta: string; valor: string; sub?: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
      <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>{etiqueta}</div>
      <div style={{ fontSize: 'var(--fs-500)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
      {sub && <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>{sub}</div>}
    </div>
  );
}

function Barra({ prog, real, atrasado }: { prog: number; real: number; atrasado: boolean }) {
  return (
    <div style={{ position: 'relative', height: 6, background: 'var(--bg-raised)', borderRadius: 3, marginTop: 4 }}>
      <div style={{ position: 'absolute', inset: 0, width: `${real}%`, background: atrasado ? 'var(--bad)' : 'var(--ok)', borderRadius: 3 }} />
      <div style={{ position: 'absolute', top: -2, bottom: -2, left: `calc(${prog}% - 1px)`, width: 2, background: 'var(--text-primary)' }} />
    </div>
  );
}
